import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect
} from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import DialogTransition from 'src/components/DialogTransition';
import {
  InvoiceTypes,
  CFDIListActions,
  CancellationMotive
} from 'src/utils/constants/cfdi';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import CurrentStep from './CurrentStep';
import makeInitialState from './initialState';
import useStyles from './styles';

const CancelCFDIDialog = ({
  open,
  onClose,
  onSubmit,
  createdAt,
  amount,
  relatedInvoiceData
}) => {
  const stableOnSubmit = useCallback(
    data => {
      onSubmit(data);
    },
    [onSubmit]
  );
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [step, setStep] = useState(0);
  const initialState = useMemo(() => makeInitialState(relatedInvoiceData), [
    relatedInvoiceData
  ]);
  const [formData, setFormData] = useState(initialState);
  const resetRef = useRef();

  useEffect(() => {
    if (open) {
      setStep(0);
      setFormData(initialState);
    }
  }, [open, initialState]);

  const handleCloseForm = () => {
    if (resetRef.current) {
      resetRef.current();
    }
    setFormData(initialState);
    onClose();
  };
  const handleNext = newData => {
    if (
      step === 0 &&
      newData?.motive === CancellationMotive.ISSUED_WITH_ERRORS_WITH_RELATION
    ) {
      setFormData(prev => ({ ...prev, ...newData }));
      setStep(1);
    } else {
      onSubmit({ ...formData, ...newData });
      onClose();
    }
  };
  const { t } = useTranslation();

  const handleBackOrClose = useCallback(() => {
    switch (step) {
      case 0:
        setFormData(initialState);
        if (resetRef.current) {
          resetRef.current();
        }
        handleCloseForm();
        break;
      case 1:
        setStep(step - 1);
        break;
      default:
        setStep(step - 1);
        break;
    }
  }, [dispatch, step, setStep, handleCloseForm]);

  if (!open) return null;

  return (
    <Dialog
      disableEscapeKeyDown
      classes={{ paper: classes.dialogRoot }}
      open={open}
      keepMounted
      style={{ marginBottom: '1.5rem' }}
      TransitionComponent={DialogTransition}
    >
      <DialogTitle
        className={classes.header}
        id="assign-kam-dialog-title"
        variant="h2"
        color="secondary"
      >
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={() => {
            handleCloseForm();
          }}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        {relatedInvoiceData?.invoiceType === InvoiceTypes.PAYMENT
          ? t(`ENUMS.CFDI_LIST_ACTIONS.${CFDIListActions.CANCEL_RECEIPT}`)
          : t(`ENUMS.CFDI_LIST_ACTIONS.${CFDIListActions.CANCEL_INVOICE}`)}
      </DialogTitle>
      <DialogContent>
        <CurrentStep
          resetRef={resetRef}
          activeStep={step}
          data={formData}
          onNext={handleNext}
          onBack={handleBackOrClose}
          onSubmit={stableOnSubmit}
          open={open}
          createdAt={createdAt}
          amount={amount}
          onClose={handleCloseForm}
        />
      </DialogContent>
    </Dialog>
  );
};

CancelCFDIDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  createdAt: PropTypes.any,
  amount: PropTypes.any,
  relatedInvoiceData: PropTypes.any
};

export default CancelCFDIDialog;
